import React, { useState, useEffect } from 'react';
import { Logo } from '../layout/Logo';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../context/AuthContext';
import { DateInput } from '../ui/DateInput';

export function AuthModal() {
  const { currentUser, signIn, signUp, resetPassword } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    country: '',
    birthdate: '',
    email: '',
    password: '',
  });
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');
  const [loginError, setLoginError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Show modal only if user is not authenticated
  useEffect(() => {
    if (!currentUser) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [currentUser]);

  async function handleSignin() {
    setLoading(true);
    try {
      await signIn(email, password);
      // App re-renders when currentUser updates - modal closes automatically
    } catch (err: any) {
      console.error('Sign in error:', err);
      setLoginError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup() {
    setSignupError('');
    setSignupSuccess('');

    const errors = validateSignup(formData);
    if (errors) {
      setSignupError(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await signUp(formData.email, formData.password, formData.name, formData.lastName, formData.birthdate);

      if (response.data?.user) {
        setSignupSuccess('Account created successfully!');
        // Clear form after success
        setFormData({
          name: '',
          lastName: '',
          country: '',
          birthdate: '',
          email: '',
          password: '',
        });
        // Switch to login tab after short delay
        setTimeout(() => {
          setActiveTab('login');
          setSignupSuccess('');
        }, 1500);
      } else if (response.error) {
        setSignupError(response.error.message || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Sign up error:', err);
      setSignupError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword() {
    setLoading(true);
    try {
      const response = await resetPassword(email);
      if (response.error) {
        throw new Error(response.error.message || 'Failed to send reset email');
      }
      setResetEmail(email);
      setResetSuccess(true);
      return response;
    } catch (err: any) {
      console.error('Reset error:', err);
      setResetError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={showModal}
        onClose={() => {}}
        title="Authentication Required"
        showCloseButton={false}
      >
        <div className="space-y-4">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setActiveTab('login')}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'login' ? 'border-b-2 border-primary' : 'text-on-surface-variant'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                setActiveTab('signup');
                setSignupError('');
                setSignupSuccess('');
              }}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'signup' ? 'border-b-2 border-primary' : 'text-on-surface-variant'
              }`}
            >
              Create Account
            </button>
            <button
              onClick={() => setActiveTab('forgot')}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'forgot' ? 'border-b-2 border-primary' : 'text-on-surface-variant'
              }`}
            >
              Forgot Password
            </button>
          </div>

          {activeTab === 'login' && <LoginForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} loading={loading} onSignin={handleSignin} loginError={loginError} setLoginError={setLoginError} onSetTab={setActiveTab} />}
          {activeTab === 'signup' && <SignupForm formData={formData} setFormData={setFormData} onSignup={handleSignup} signupError={signupError} signupSuccess={signupSuccess} onSetTab={setActiveTab} />}
          {activeTab === 'forgot' && <ForgotForm email={resetEmail || email} setEmail={setEmail} onReset={handleResetPassword} onSetTab={setActiveTab} resetLoading={resetLoading} resetError={resetError} resetSuccess={resetSuccess} />}
        </div>
      </Modal>
    </>
  );
}

function validateSignup(data: {
  name: string;
  lastName: string;
  country: string;
  birthdate: string;
  email: string;
  password: string;
}): string | null {
  // Check all fields are filled
  if (!data.name.trim()) return 'First name is required';
  if (!data.lastName.trim()) return 'Last name is required';
  if (!data.email.trim()) return 'Email is required';
  if (!data.password) return 'Password is required';
  if (!data.country) return 'Country is required';
  if (!data.birthdate) return 'Birthdate is required';

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.email)) return 'Please enter a valid email address';

  // Validate password strength (at least 8 characters, 1 special char, 1 number)
  if (data.password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(data.password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(data.password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(data.password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(data.password)) return 'Password must contain at least one special character';

  // Validate birthdate (must be 18+)
  const birthDate = new Date(data.birthdate);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  if (age < 18) return 'You must be at least 18 years old to create an account';

  return null;
}

function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  onSignin,
  loginError,
  setLoginError,
  onSetTab
}: {
  email: string;
  setEmail: (e: string) => void;
  password: string;
  setPassword: (e: string) => void;
  loading: boolean;
  onSignin: () => void;
  loginError: string;
  setLoginError: (error: string) => void;
  onSetTab: (tab: 'login' | 'signup' | 'forgot') => void;
}) {
  const validateLogin = () => {
    // Email validation - must have @ and domain with at least 2 chars after dot
    if (!email.trim()) {
      setLoginError('Email is required');
      return false;
    }
    // More strict email pattern: user@domain.tld (domain must have at least one dot)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setLoginError('Invalid email format');
      return false;
    }

    // Password validation - minimum 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char
    if (!password || password.length < 8) {
      setLoginError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setLoginError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setLoginError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setLoginError('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      setLoginError('Password must contain at least one special character');
      return false;
    }

    setLoginError('');
    return true;
  };

  const handleSignin = () => {
    if (validateLogin()) {
      onSignin();
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium text-on-surface-variant">Email</label>
        <input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-on-surface-variant">Password</label>
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
        />
      </div>
      {loginError && <div className="text-xs text-error">{loginError}</div>}
      <button
        onClick={handleSignin}
        disabled={loading || !email.trim() || !password || password.length < 8}
        className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast disabled:opacity-50"
      >
        {loading ? 'Signing In...' : 'Sign In'}
      </button>
      <p className="text-center text-sm">
        <button className="text-primary hover:underline" onClick={() => onSetTab('forgot')}>Forgot password?</button>
      </p>
    </div>
  );
}

function SignupForm({ formData, setFormData, onSignup, signupError, signupSuccess, onSetTab }: {
  formData: { name: string; lastName: string; country: string; birthdate: string; email: string; password: string };
  setFormData: React.Dispatch<React.SetStateAction<{ name: string; lastName: string; country: string; birthdate: string; email: string; password: string }>>;
  onSignup: () => void;
  signupError: string;
  signupSuccess: string;
}) {
  const [consentMedical, setConsentMedical] = useState(false);
  const [consentData, setConsentData] = useState(false);

  const allConsent = consentMedical && consentData;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (allConsent) {
      await onSignup();
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">First Name</label>
          <input
            type="text"
            placeholder="Your first name"
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">Last Name</label>
          <input
            type="text"
            placeholder="Your last name"
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">Email</label>
          <input
            type="email"
            placeholder="your@email.com"
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">Password</label>
          <input
            type="password"
            placeholder="At least 8 characters"
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
          />
        </div>
        <DateInput
          label="Birthdate"
          value={formData.birthdate}
          onChange={(dateStr) => handleChange('birthdate', dateStr)}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">Country</label>
          <select
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
            value={formData.country}
            onChange={(e) => handleChange('country', e.target.value)}
          >
            <option value="">Select your country</option>
            <option value="ZA">South Africa 🇿🇦</option>
            <option value="US">United States 🇺🇸</option>
            <option value="UK">United Kingdom 🇬🇧</option>
            <option value="AU">Australia 🇦🇺</option>
            <option value="CA">Canada 🇨🇦</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={consentMedical}
            onChange={(e) => setConsentMedical(e.target.checked)}
          />
          <span className="text-xs text-on-surface-variant">
            I understand this app is not medical advice. I will consult a doctor before extended
            fasting.
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={consentData}
            onChange={(e) => setConsentData(e.target.checked)}
          />
          <span className="text-xs text-on-surface-variant">
            I consent to 222fasting storing my health data.
          </span>
        </label>
      </div>

      {signupError && <div className="rounded-lg bg-error-container px-3 py-2 text-xs text-error">{signupError}</div>}
      {signupSuccess && <div className="rounded-lg bg-primary-container px-3 py-2 text-xs text-primary-contrast">{signupSuccess}</div>}

      <button
        onClick={handleSubmit}
        className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast disabled:opacity-50"
        disabled={!allConsent || !formData.email.trim() || formData.password.length < 8}
      >
        Create Account
      </button>
      <p className="text-center text-xs text-on-surface-variant">
        Already have an account? <button className="text-primary hover:underline" onClick={() => {}}>Sign In</button>
      </p>
    </div>
  );
}

function ForgotForm({ email, setEmail, onReset, onSetTab }: { email: string; setEmail: (e: string) => void; onReset: () => Promise<void>; onSetTab: (tab: 'login' | 'signup' | 'forgot') => void; }) {
  const [resetEmail, setResetEmail] = useState(email);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const validateEmail = (email: string) => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const handleReset = async () => {
    const error = validateEmail(resetEmail);
    if (error) {
      setResetError(error);
      return;
    }
    setResetError('');
    setResetLoading(true);
    try {
      await onReset();
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-primary-container px-4 py-3">
          <p className="text-sm text-primary-contrast">
            Password reset link sent to <strong>{resetEmail}</strong>. Please check your email and click the link to reset your password.
          </p>
        </div>
        <button
          onClick={() => onSetTab('login')}
          className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium text-on-surface-variant">
          Email Address
        </label>
        <input
          type="email"
          placeholder="your@email.com"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          className="w-full rounded-lg border border-outline bg-surface px-3 py-2"
        />
      </div>
      {resetError && <div className="text-xs text-error">{resetError}</div>}
      <button
        onClick={handleReset}
        disabled={resetLoading}
        className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast disabled:opacity-50"
      >
        {resetLoading ? 'Sending...' : 'Send Reset Link'}
      </button>
      <p className="text-center text-xs text-on-surface-variant">
        Remember your password? <button className="text-primary hover:underline" onClick={() => onSetTab('login')}>Sign In</button>
      </p>
    </div>
  );
}
